import { BookingForm, bookingStore } from "../../../config/store/bookingStore";
import { Dispatch } from "react";
import {
  CalendarEvent,
  Hours,
  ServiceEntity,
} from "../../../../constants/model/models";
import {
  addUnitWithStringDate,
  addZero,
  extractHours,
  timeIsAfter,
  timeIsBefore,
} from "../../../../services/dateAndTime/dateAndTimeUtil";
import { BACKEND_URL } from "../../../../constants/env";
import {
  queryClient,
  QueryKeys,
} from "../../../../hooks/react-query/query-client";
import { useRouter } from "next/router";
import { AppointmentDTO } from "../../../../hooks/react-query/useAppointmentList";
import { useUser } from "../../../../hooks/react-query/useUser";
import { useJWT } from "../../../../hooks/react-query/useJWT";

export function useAppointment() {
  const router = useRouter();
  const { user } = useUser();
  const { jwt } = useJWT();
  const {
    stepperFields,
    updateBookingForm,
    updateIndex,
    resetStepperFields,
    bookingForm,
    setAmount,
    setPrepaymentStep,
    updateErrorMessage,
  } = bookingStore((state) => state);

  async function submitAppointment(
    token: string,
    bookingForm: BookingForm,
    setProcessing: Dispatch<boolean>
  ): Promise<string | AppointmentDTO> {
    try {
      setProcessing(true);

      // @ts-ignore
      if (bookingForm.slot && bookingForm.slot.noDuration) {
        const bookedServices = bookingForm.listOfServices;
        if (!bookedServices || bookedServices.length === 0) {
          setProcessing(false);
          return "CHOOSE_SERVICE";
        }

        const date: string = bookingForm.date;
        let time: Hours = bookingForm.time;
        if (typeof time === 'undefined')
          time = bookingForm.hour;
        bookingForm.time = time;
        let duration: number = 0;
        const start: string = `${date}T${addZero(time.hour)}:${addZero(
          time.minute
        )}`;

        bookedServices.forEach((service: string) => {
          const serviceDuration = service.split("###")[1];
          if (serviceDuration) duration = duration + +serviceDuration;
        });
        const end: string = addUnitWithStringDate(start, duration, "minute");

        if (
          timeIsBefore(start, bookingForm.slotStart) ||
          timeIsAfter(end, bookingForm.slotEnd)
        ) {
          setProcessing(false);
          return "NO_SUGGESTED_SLOT";
        }
      } else {
        bookingForm.time = extractHours(bookingForm.slot.dateTime.toString());
      }

      bookingForm.online = stepperFields.format === "online";

      try {
        const response = await fetch(`${BACKEND_URL}/user/appointments`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            accessToken: token,
          },
          body: JSON.stringify(bookingForm),
        });

        const jsonResponse = await response.json();
        return await processResponse(jsonResponse, setProcessing);
      } catch (err) {
        setProcessing(false);
        return "BOOKING_ERROR";
      }
    } catch (err) {
      setProcessing(false);
      console.log(err);
      return "BOOKING_ERROR";
    }
  }

  async function processResponse(
    result: any,
    setProcessing: Dispatch<boolean>
  ): Promise<string | AppointmentDTO> {
    setProcessing(false);
    if (result.appointment) {
      return result as AppointmentDTO;
    }

    if (result.suggestedTiming) {
      return "SURPASSED_DURATION";
    }
    /*process error messages*/
    if (result.beforeNotice) {
      return "BEFORE_NOTICE";
    }

    if (result.message) {
      return result.message as string;
    } else {
      return "BOOKING_ERROR";
    }
  }

  async function redirectOnSuccess() {
    await queryClient.invalidateQueries(QueryKeys.APPOINTMENTS);
    await router.push("/user/appointments/book/success");
    updateBookingForm(undefined);
    resetStepperFields();
    updateIndex(0);
    setAmount(0);
    setPrepaymentStep(false);
  }

  function calculateAmountToPay() {
    if (bookingForm && bookingForm.slot) {
      const slot: CalendarEvent = bookingForm.slot;
      if (
        !slot.noDuration &&
        slot.mandatoryPrepayment &&
        slot.price &&
        slot.price > 0
      ) {
        setAmount(+slot.price.toFixed(2));
      } else if (
        slot.noDuration &&
        slot.services &&
        slot.services.length > 0 &&
        bookingForm.listOfServices
      ) {
        //determine which services have been booked and their price
        const bookedServices: ServiceEntity[] = [];
        let amountToPay = 0;
        bookingForm.listOfServices.forEach((service) => {
          slot.services.forEach((slotService) => {
            if (
              service.toLowerCase().split("###")[0] ===
              slotService.service.toLowerCase()
            ) {
              bookedServices.push(slotService);
            }
          });
        });
        bookedServices.forEach((bookedService) => {
          if (bookedService.mandatoryPrepayment && bookedService.price) {
            amountToPay = amountToPay + bookedService.price;
          }
        });
        setAmount(+amountToPay.toFixed(2));
      }
    }
  }

  async function submitBooking(
    bookingForm: BookingForm,
    setProcessing: Dispatch<boolean>
  ) {
    if (user && jwt) {
      const response = await submitAppointment(jwt, bookingForm, setProcessing);

      if (typeof response === "string") {
        updateErrorMessage(response.toString());
        await router.push("/user/appointments/book/error");
      } else {
        if (response.payment?.paymentUrl) {
          // @ts-ignore
          window.location = response.payment?.paymentUrl;
        } else {
          await redirectOnSuccess();
        }
      }
    }
  }

  return {
    calculateAmountToPay,
    submitBooking,
  };
}
